import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { fetchThemeData } from "../../admin/themeSlice";
import { selectAdminToken } from "../../admin/sessionSlice";
import "../fonts/breuer-headline.css";
import "./leaderboard.css";
import leaderboardHeader from "../../img/leaderboard.png";

function readSessionUserId() {
  try {
    const raw = sessionStorage.getItem("userData");
    if (!raw) return "";
    const u = JSON.parse(raw);
    const id = u?.userId ?? u?.userid ?? u?.id;
    if (id == null || id === "") return "";
    return String(id).trim();
  } catch {
    return "";
  }
}

function normalizeLeaderboardRow(row, index) {
  if (!row || typeof row !== "object") {
    return {
      key: `row-${index}`,
      rank: index + 1,
      userId: "",
      name: "—",
      points: 0,
      time: "00:00",
    };
  }
  const name =
    row.name ??
    row.player_name ??
    row.user_name ??
    row.display_name ??
    (row.email ? String(row.email).split("@")[0] : null) ??
    "—";
  const rawPoints = row.points ?? row.score ?? row.total_score ?? row.point ?? 0;
  const n = Number(rawPoints);
  const points = Number.isFinite(n) ? n : 0;
  const rawTime = row.time ?? row.display_time ?? "00:00";
  const time = String(rawTime).trim() || "00:00";
  const rankNum = Number(row.rank ?? row.rk);
  const rank = Number.isFinite(rankNum) && rankNum > 0 ? rankNum : index + 1;
  const userId =
    row.userId != null && row.userId !== ""
      ? String(row.userId)
      : row.userid != null && row.userid !== ""
        ? String(row.userid)
        : "";

  return {
    key: String(row.userId ?? row.userid ?? row.rank ?? row.id ?? index),
    rank,
    userId,
    name: String(name),
    points,
    time,
  };
}

const LEADERBOARD_TOP_N = 5;

const PLAYER_AVATARS = [
  { icon: "👑", tone: "navy" },
  { icon: "⛰", tone: "blue" },
  { icon: "🎯", tone: "purple" },
  { icon: "⚡", tone: "orange" },
  { icon: "🧠", tone: "teal" },
  { icon: "📅", tone: "sky" },
  { icon: "☕", tone: "rose" },
  { icon: "⚖", tone: "green" },
  { icon: "📖", tone: "violet" },
  { icon: "📈", tone: "cyan" },
];

function buildDisplayRows(rows, currentUserId) {
  if (!rows.length) return [];

  const sorted = [...rows].sort((a, b) => a.rank - b.rank);
  const top = sorted
    .filter((r) => r.rank >= 1 && r.rank <= LEADERBOARD_TOP_N)
    .slice(0, LEADERBOARD_TOP_N);

  if (!currentUserId) return top;

  const userRow = sorted.find(
    (r) => r.userId && String(r.userId) === String(currentUserId)
  );

  if (!userRow || userRow.rank <= LEADERBOARD_TOP_N) return top;

  return [...top, { ...userRow, isPinnedViewer: true }];
}

function ClockIcon() {
  return (
    <svg className="lb-icon lb-icon--clock" viewBox="0 0 20 20" aria-hidden>
      <circle cx="10" cy="10" r="8" fill="currentColor" opacity="0.18" />
      <circle cx="10" cy="10" r="8" fill="none" stroke="currentColor" strokeWidth="1.5" />
      <path d="M10 5.5V10l3 2" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
    </svg>
  );
}

function StarIcon() {
  return (
    <svg className="lb-icon lb-icon--star" viewBox="0 0 20 20" aria-hidden>
      <path
        d="M10 2.5l1.8 4.2 4.6.4-3.5 3 1 4.5L10 12.8 5.1 14.6l1-4.5-3.5-3 4.6-.4L10 2.5z"
        fill="currentColor"
      />
    </svg>
  );
}

function RankDisplay({ rank }) {
  if (rank === 1) {
    return (
      <span className="lb-medal lb-medal--gold" aria-label="Rank 1">
        <span className="lb-medal__ring" />
        <span className="lb-medal__num">1</span>
      </span>
    );
  }
  if (rank === 2) {
    return (
      <span className="lb-medal lb-medal--silver" aria-label="Rank 2">
        <span className="lb-medal__ring" />
        <span className="lb-medal__num">2</span>
      </span>
    );
  }
  if (rank === 3) {
    return (
      <span className="lb-medal lb-medal--bronze" aria-label="Rank 3">
        <span className="lb-medal__ring" />
        <span className="lb-medal__num">3</span>
      </span>
    );
  }
  return <span className="lb-rank-num">{rank}</span>;
}

function PlayerAvatar({ rank }) {
  const avatar = PLAYER_AVATARS[(Math.max(rank, 1) - 1) % PLAYER_AVATARS.length];
  return (
    <span className={`lb-avatar lb-avatar--${avatar.tone}`} aria-hidden>
      {avatar.icon}
    </span>
  );
}

function Leaderboard() {
  const dispatch = useDispatch();
  const adminToken = useSelector(selectAdminToken);
  const { currentTheme } = useSelector((s) => s.theme);

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentUserId, setCurrentUserId] = useState(() => readSessionUserId());

  const backendBase = useMemo(
    () => String(process.env.REACT_APP_BACKEND_URL || "").replace(/\/+$/, ""),
    []
  );

  useEffect(() => {
    dispatch(fetchThemeData({ themeId: currentTheme || null }));
  }, [dispatch, currentTheme]);

  useEffect(() => {
    document.body.classList.add("leaderboard-page");
    return () => {
      document.body.classList.remove("leaderboard-page");
    };
  }, []);

  const loadLeaderboard = useCallback(async () => {
    if (!adminToken || !backendBase) {
      setLoading(false);
      setError(!adminToken ? "Not signed in." : "Server URL not configured.");
      return;
    }
    setLoading(true);
    setError(null);
    const uid = readSessionUserId();
    setCurrentUserId(uid);
    try {
      const res = await fetch(`${backendBase}/getLeaderboard`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${adminToken}`,
        },
        body: JSON.stringify({
          userId: uid || undefined,
          limit: LEADERBOARD_TOP_N,
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (res.ok && data.success && Array.isArray(data.data)) {
        setRows(data.data.map(normalizeLeaderboardRow));
      } else {
        setError(data.error || data.message || "Failed to load leaderboard.");
      }
    } catch (e) {
      setError(e.message || "Failed to load leaderboard.");
    } finally {
      setLoading(false);
    }
  }, [adminToken, backendBase]);

  useEffect(() => {
    loadLeaderboard();
  }, [loadLeaderboard]);

  const displayRows = useMemo(
    () => buildDisplayRows(rows, currentUserId),
    [rows, currentUserId]
  );

  return (
    <div className="lb-page">
      <div className="lb-layout">
        <img
          src={leaderboardHeader}
          alt="Leaderboard"
          className="lb-header-img"
        />

        <div className="lb-card">
          <div className="lb-card__body">
          {loading ? (
            <p className="lb-status">Loading scores…</p>
          ) : error ? (
            <p className="lb-status lb-status--error">{error}</p>
          ) : rows.length === 0 ? (
            <p className="lb-status">No scores yet.</p>
          ) : (
            <>
              <div
                className="lb-table-scroll"
                role="region"
                aria-label="Leaderboard rankings"
                tabIndex={0}
              >
                <div className="lb-table-inner">
              <div className="lb-colhead" aria-hidden>
                <span className="lb-colhead__cell lb-colhead__cell--rank">Rank</span>
                <span className="lb-colhead__cell lb-colhead__cell--player">Player</span>
                <span className="lb-colhead__cell lb-colhead__cell--time">
                  <ClockIcon />
                  Time
                </span>
                <span className="lb-colhead__cell lb-colhead__cell--points">
                  <StarIcon />
                  Points
                </span>
              </div>

              <ul className="lb-rows">
                {displayRows.map((r) => {
                  const isYou =
                    currentUserId &&
                    r.userId &&
                    String(r.userId) === String(currentUserId);
                  return (
                    <li
                      key={r.isPinnedViewer ? `pinned-${r.key}` : r.key}
                      className={`lb-row${r.rank === 1 ? " lb-row--first" : ""}${
                        isYou ? " lb-row--you" : ""
                      }${r.isPinnedViewer ? " lb-row--pinned" : ""}`}
                    >
                      <div className="lb-row__rank">
                        <RankDisplay rank={r.rank} />
                      </div>
                      <div className="lb-row__player">
                        <PlayerAvatar rank={r.rank} />
                        <span className="lb-row__name">{r.name}</span>
                      </div>
                      <div className="lb-row__time">
                        <ClockIcon />
                        <span>{r.time}</span>
                      </div>
                      <div className="lb-row__points">
                        <StarIcon />
                        <span>{r.points.toLocaleString()}</span>
                      </div>
                    </li>
                  );
                })}
              </ul>
                </div>
              </div>
            </>
          )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default Leaderboard;
